import { ActionBlock } from "./ActionBlock";

export const ActionStep = {
  Activate: 'Activate',
  Update: 'Update',
  Deactivate: 'Deactivate',
  Finish: 'Finish'
} as const;
type ActionStepKeys = keyof typeof ActionStep;
type ActionStepValues = typeof ActionStep[ActionStepKeys];

/**
 * Generic action runner allowing multiple actions to be queued up and run after one another.
 *
 * Enhancement Ideas:
 * Allow ActionBlocks to return ActionBlocks to run as sub-blocks (call stack).
 * Allow "goto" esc order/repeat clauses.
 * Allow dynamic block insertion.
 * Allow data to be passthrough from one step to next step.
 * Allow initial setup data to be passed to each step.
 *
 */
export class ActionRunner {
  protected actions: ActionBlock[];
  protected actionIndex: number;
  protected currentAction: ActionBlock;

  protected isRunning: boolean;
  protected currentStep: ActionStepValues;
  protected isUpdating: boolean;
  protected canContinue: boolean;
  protected callback: (params: any) => void;

  constructor() {
    this.actions = [];
    this.actionIndex = 0;
    this.currentAction = this.actions[this.actionIndex];

    this.isRunning = false;
    this.currentStep = ActionStep.Activate;
    this.isUpdating = false;
    this.canContinue = false;
    this.callback = this.advanceCallback.bind(this);
  }

  get IsRunning(): boolean {
    return this.isRunning;
  }

  get CurrentAction(): string | undefined {
    if (this.isRunning) return this.currentAction.actionName;
    else return;
  }

  get CurrentActionIndex(): number {
    return this.actionIndex;
  }

  get CurrentStep(): ActionStepValues {
    return this.currentStep;
  }

  public setActions(actions: ActionBlock[]) {
    this.actions = actions;
    this.isRunning = false;
  }

  public start() {
    this.isRunning = true;
    this.currentStep = ActionStep.Activate;
    this.isUpdating = false;
    this.canContinue = false;
    this.actionIndex = 0;
    this.currentAction = this.actions[this.actionIndex];

    if (this.actions.length > 0) this.advance();
    else this.isRunning = false;
  }

  public stop() {
    this.isRunning = false;
  }

  // insertAfter(action: ActionBlock);

  public update(time: number, delta: number) {
    if (!this.isRunning) return;

    

    if (this.isUpdating && this.actions != null && this.actions.length > 0 && this.actionIndex < this.actions.length) {
      let result = this.actions[this.actionIndex]?.onUpdate?.(this, time, delta, this.callback) ?? true;
      this.isUpdating = !result;
    }

    if (!this.canContinue) return;
    this.advance();
  }

  protected advanceCallback(params: any) {
    this.canContinue = true;
  }

  protected advance() {

    switch (this.currentStep) {
      case ActionStep.Activate:
        this.currentStep = ActionStep.Update;
        if (this.currentAction.onActivate) {
          this.canContinue = false;
          this.currentAction?.onActivate(this, this.callback);
        } else {
          this.advance();
        }
        break;

      case ActionStep.Update:
        this.currentStep = ActionStep.Deactivate;
        if (this.currentAction.onUpdate) {
          this.isUpdating = true;
          this.canContinue = false;
        } else {
          this.advance();
        }
        break;

      case ActionStep.Deactivate:
        this.currentStep = ActionStep.Finish;
        if (this.currentAction.OnDeactivate) {
          this.canContinue = false;
          this.currentAction.OnDeactivate(this, this.callback);
        } else {
          this.advance();
        }
        break;

      case ActionStep.Finish:
        if (this.actionIndex < this.actions.length - 1) {
          this.actionIndex++;
          this.currentAction = this.actions[this.actionIndex];
          this.currentStep = ActionStep.Activate;
        } else {
          this.stop();
        }
        break;

      default:
        break;
    }
  }

  public clear()
  {
    this.actions = [];
  }
}
